import { useEffect, useRef, useState } from 'react'
import { useForm, useWatch } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useQueryClient, type QueryKey } from '@tanstack/react-query'
import { RefreshCw } from 'lucide-react'
import { api } from '@/api/client'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Form, FormField, FormItem, FormControl } from '@/components/ui/form'
import { ToastAction } from '@/components/ui/toast'
import { useToast } from '@/hooks/use-toast'
import { formatLocaleDateTime } from '@/lib/format-utils'

// Thin, form-local schema (not the raw contracts-package schema — this
// dialog's actual input shape is just the edited draft text) for the
// thank-you message textarea. Mirrors the manual `if (!editedText.trim())`
// guard the Send button used to enforce. Deliberately a non-transforming
// refine, not `.trim().min(1)` — Zod's `.trim()` transforms the parsed
// value itself, which would silently strip intentional leading/trailing
// whitespace or line breaks from the message actually sent to the guest
// (Codex PR review); this only checks non-blankness, it never rewrites
// the text.
const thankYouMessageSchema = z.object({
  text: z.string().refine(value => value.trim().length > 0, 'A thank-you message is required'),
})
type ThankYouMessageValues = z.infer<typeof thankYouMessageSchema>

type ThankYouSuggestionStatus = 'queued' | 'completed' | 'failed'

interface ThankYouSuggestion {
  status: ThankYouSuggestionStatus
  version: number
  message_text?: string
  error?: string | null
  sent_at?: string | null
  sent_by?: string | null
}

type Phase = 'loading' | 'ready' | 'generating' | 'error'
type ErrorStage = 'load' | 'generate'

interface ThankYouMessageDialogProps {
  reservationId?: string | null
  guestName?: string | null
  open: boolean
  onOpenChange: (open: boolean) => void
  /**
   * A send succeeded on the server. admin-api marks the chase
   * host-reviewed off that send (the note says "I just left you a 5-star
   * review", so sending it IS that assertion - natalan/admin-api#535), so
   * the page's own view of the row is stale the moment this fires and has
   * to be re-read. Called once per successful send, whatever the dialog is
   * showing by then: the server-side fact does not depend on this dialog
   * still being open, or still being open for this reservation.
   */
  onSent?: () => void
}

// A thank-you draft is now generated proactively (see admin-api's
// review-chase.js checkout-today trigger), the same "usually already
// sitting in KV by the time you look" pattern the guest-review suggestion
// uses (SuggestedReviewBlock) — so most of the time the GET below already
// finds a "completed" draft. A "queued" result means that background job
// (or a regenerate) is already in flight, so poll for it instead of firing
// off a redundant generation on top of one already running. A genuinely
// null result (nothing ever triggered — e.g. a reservation that predates
// this feature) still falls back to generating one here immediately, same
// as before this dialog polled at all.
const POLL_INTERVAL_MS = 3000
// Mirrors SuggestedReviewBlock's identical budget/reasoning for the
// guest-review suggestion's own "queued" poll, which this dialog used to
// have no equivalent of since it always awaited a synchronous regenerate
// call instead.
const MAX_ACTIVE_GENERATION_POLL_ATTEMPTS = 10

// How long a send OUTCOME toast (success/failure) stays protected from
// being replaced by the next toast this dialog shows — see
// outcomeToastScheduleQueryKey's own comment, on the component, for why.
const OUTCOME_MIN_VISIBLE_MS = 1500

function targetVersionQueryKey(reservationId: string | null | undefined): QueryKey {
  return ['thank-you-suggestion-target-version', reservationId]
}

// The wall-clock moment a send OUTCOME toast (success/failure) was LAST
// actually shown (Codex PR review, seventh and eighth passes). A ref was
// tried first, but a background send keeps running (and can still call
// queueOutcomeToast / afterOutcomeProtection) after the operator
// navigates away and the component that started it unmounts; if they
// navigate back, the new mount gets a brand-new ref with no memory of
// that still-running send's claimed protection window, so the two can
// freely evict each other's toasts with no coordination at all —
// including a NEW failure's Retry action, evicted by an ORPHANED
// "Sending…" callback the operator can no longer see or act on. The
// query cache (mirrors targetVersionQueryKey above) solves this the same
// way it already does for that case: pinned to the router-level
// QueryClient, which outlives any one mount, so a second instance can
// still see and respect a still-active window from an earlier one. It
// also happens to keep tests isolated from each other, since each test's
// renderWithProviders creates its own QueryClient by default.
//
// Tracks when a toast was actually shown, not a reserved future slot
// (Codex PR review, eleventh pass): an earlier design pre-computed each
// outcome's own display time when it was QUEUED, based on Date.now() at
// that moment. A backgrounded tab (browsers throttle setTimeout there) or
// a main thread blocked past two or more of those computed times lets
// their now-overdue callbacks fire back-to-back once it's free again —
// with no real gap between them, even though their PRE-COMPUTED times had
// been properly spaced. Re-deriving the "protected until" window from
// this fact at every fire attempt — both queueOutcomeToast and
// afterOutcomeProtection below re-check it right before actually
// displaying anything, not just when first scheduled — means the actual,
// current elapsed time always governs whether a toast is still
// protected, regardless of how delayed the callback checking it was.
function outcomeToastScheduleQueryKey(): QueryKey {
  return ['thank-you-suggestion-outcome-toast-schedule']
}

// One shared key holding a Set of reservationId strings this browser
// session knows for a fact had a thank-you message successfully sent
// (Codex PR review, fourth pass). A single component-state slot can only
// remember one reservation at a time — a second reservation sent
// concurrently overwrites the first one's lock — and component state
// doesn't survive the operator navigating away from /review-chase and
// back, which unmounts and remounts ThankYouMessageDialog with nothing
// retained. The query cache (mirrors targetVersionQueryKey just above)
// survives both: keyed per reservation instead of a single slot, and
// pinned to the router-level QueryClient instead of this component's own
// state.
//
// Keyed by reservation ALONE, not (reservation, version) (Codex PR
// review, ninth pass): a thank-you is sent to the GUEST once, not once
// per suggestion version — if a version-1 send is still in flight when
// the operator reopens, Regenerates to version 2, and sends THAT before
// version 1's response arrives, a per-version lock only ever protects
// whichever version each individual send targeted. Version 1 succeeding
// would lock "reservationId:1", but the version-2 send is checked against
// "reservationId:2" — a different, still-unlocked key — so it goes
// through and a genuinely second real message reaches the guest, even
// though the operator never asked for two. (This corrects the
// "intentional tradeoff" reasoning in handleRegenerate's own comment
// below, written before this lock existed to actually prevent it — once
// a lock exists specifically to stop duplicate real sends, silently
// allowing a same-reservation duplicate through a version mismatch isn't
// a tradeoff, it's the exact bug the lock is for.)
function confirmedSentQueryKey(): QueryKey {
  return ['thank-you-suggestion-confirmed-sent']
}

// The pending idempotency key + edited body for a reservation's not-yet-
// confirmed-successful send, keyed PER RESERVATION rather than one shared
// slot (Codex PR review, fifth pass). A single slot meant opening a
// SECOND reservation's dialog while a first one's send was still in
// flight discarded the first one's retry state outright — if that first
// send then failed ambiguously, reopening it no longer recognized the
// retry as the same request and minted a brand-new key, risking a
// duplicate real message if the original had actually reached Uplisting.
function pendingRetryQueryKey(reservationId: string | null | undefined): QueryKey {
  return ['thank-you-suggestion-pending-retry', reservationId]
}

// One shared key holding a Set of reservationId strings with a send
// CURRENTLY in flight — added the instant performSend dispatches, removed
// the instant it settles (success or failure). confirmedSent alone only
// locks a reservation once a send is KNOWN to have succeeded (Codex PR
// review, tenth pass): if the operator reopens the SAME reservation,
// edits or Regenerates, and confirms again before the FIRST attempt's
// response has arrived, confirmedSent is still empty at that second
// confirm — nothing yet says this reservation is unsendable — so it
// dispatches a second request under its own new idempotency key while the
// first is still pending. Both can then independently succeed, and the
// guest gets two real messages. This closes that specific window:
// checked alongside confirmedSent at every dispatch point, it blocks a
// second confirm for as long as an earlier one for the same reservation
// hasn't settled yet, regardless of what that earlier one's eventual
// outcome turns out to be.
function sendInFlightQueryKey(): QueryKey {
  return ['thank-you-suggestion-send-in-flight']
}

/**
 * "Send a thank-you message" dialog for a Review Chase row whose guest has
 * already been reviewed. Displays the AI draft (classifying family vs.
 * group and pulling any companion names out of the guest thread) — usually
 * already generated proactively by the time this opens, see above — lets
 * the operator edit it, then sends on a single Send click — this is a real
 * message to a real guest via Uplisting, never sent automatically (root
 * CLAUDE.md: "Reply suggestions: operators always review before sending —
 * system never auto-sends"; editing/reviewing the draft in this dialog IS
 * that review step, so Send doesn't need a second confirmation click on
 * top of it).
 */
export function ThankYouMessageDialog({
  reservationId,
  guestName,
  open,
  onOpenChange,
  onSent,
}: ThankYouMessageDialogProps) {
  const { toast } = useToast()
  const queryClient = useQueryClient()
  // The highest version we're currently waiting for a given reservation's
  // generation to reach. Stored in the query cache (mirrors
  // SuggestedReviewBlock's identical targetVersionQueryKey), NOT a plain
  // ref, so it survives this component unmounting and remounting — e.g.
  // the operator navigates away from /review-chase after Regenerate and
  // comes back before the queued version becomes visible in
  // eventually-consistent KV (Codex PR review, follow-up: a ref would be
  // recreated with nothing on remount, resurfacing the same stale-draft
  // race the ref fixed for a same-instance close/reopen). Pinned to never
  // expire via setQueryDefaults, same as SuggestedReviewBlock, since
  // nothing ever observes it via useQuery — it would otherwise be eligible
  // for garbage collection almost immediately.
  const getPendingTargetVersion = (targetReservationId: string | null | undefined) =>
    queryClient.getQueryData<number>(targetVersionQueryKey(targetReservationId)) ?? 0
  const setPendingTargetVersion = (
    targetReservationId: string | null | undefined,
    version: number
  ) => {
    const key = targetVersionQueryKey(targetReservationId)
    queryClient.setQueryDefaults(key, { gcTime: Infinity })
    queryClient.setQueryData<number>(
      key,
      Math.max(getPendingTargetVersion(targetReservationId), version)
    )
  }
  const isConfirmedSent = (targetReservationId: string | null | undefined) => {
    if (targetReservationId == null) return false
    const sent = queryClient.getQueryData<Set<string>>(confirmedSentQueryKey())
    return sent?.has(targetReservationId) ?? false
  }
  const markConfirmedSent = (targetReservationId: string) => {
    const key = confirmedSentQueryKey()
    queryClient.setQueryDefaults(key, { gcTime: Infinity })
    const next = new Set(queryClient.getQueryData<Set<string>>(key) ?? [])
    next.add(targetReservationId)
    queryClient.setQueryData<Set<string>>(key, next)
  }
  const isSendInFlight = (targetReservationId: string | null | undefined) => {
    if (targetReservationId == null) return false
    const inFlight = queryClient.getQueryData<Set<string>>(sendInFlightQueryKey())
    return inFlight?.has(targetReservationId) ?? false
  }
  const setSendInFlight = (targetReservationId: string, inFlight: boolean) => {
    const key = sendInFlightQueryKey()
    const current = queryClient.getQueryData<Set<string>>(key) ?? new Set<string>()
    if (inFlight === current.has(targetReservationId)) return
    const next = new Set(current)
    if (inFlight) {
      queryClient.setQueryDefaults(key, { gcTime: Infinity })
      next.add(targetReservationId)
    } else {
      next.delete(targetReservationId)
    }
    queryClient.setQueryData<Set<string>>(key, next)
  }
  const getPendingRetry = (targetReservationId: string | null | undefined) =>
    queryClient.getQueryData<{ idempotencyKey: string; pendingSendBody: string }>(
      pendingRetryQueryKey(targetReservationId)
    ) ?? null
  const setPendingRetry = (
    targetReservationId: string | null | undefined,
    value: { idempotencyKey: string; pendingSendBody: string } | null
  ) => {
    const key = pendingRetryQueryKey(targetReservationId)
    if (value) {
      queryClient.setQueryDefaults(key, { gcTime: Infinity })
      queryClient.setQueryData(key, value)
    } else {
      queryClient.removeQueries({ queryKey: key, exact: true })
    }
  }
  // See outcomeToastScheduleQueryKey's own comment above for why this
  // lives in the query cache rather than a ref or module state, and why
  // it tracks when a toast was actually shown rather than a pre-computed
  // future slot. use-toast.ts caps toasts at TOAST_LIMIT=1, so two shown
  // within milliseconds of each other would otherwise instantly evict one
  // another, which can hide a failure's Retry action before the operator
  // ever sees it.
  const getOutcomeToastLastShownAt = () =>
    queryClient.getQueryData<number>(outcomeToastScheduleQueryKey()) ?? 0
  const setOutcomeToastLastShownAt = (value: number) => {
    const key = outcomeToastScheduleQueryKey()
    queryClient.setQueryDefaults(key, { gcTime: Infinity })
    queryClient.setQueryData<number>(key, value)
  }
  // The wall-clock moment a toast is next free to be replaced, derived
  // from the fact above rather than stored separately — shared by both
  // queueOutcomeToast (which also claims it, by updating
  // outcomeToastLastShownAt at the moment IT shows something) and
  // afterOutcomeProtection (which only waits it out).
  const outcomeToastProtectedUntil = () => getOutcomeToastLastShownAt() + OUTCOME_MIN_VISIBLE_MS
  // Every setTimeout id currently scheduled by queueOutcomeToast /
  // afterOutcomeProtection for THIS component instance, cleared on
  // unmount below. A toast's DISPLAY timing is cosmetic, not a
  // correctness dependency — the durable facts a reopened dialog needs
  // (confirmedSent, pendingRetry) are already persisted in the query
  // cache regardless of whether any toast for that send ever renders — so
  // losing a late notification on unmount is an acceptable, recoverable
  // trade for not leaving a scheduled callback to fire later, into
  // whatever else the operator is looking at by then (or, in tests, into
  // a completely unrelated later test: toast() is a module-level
  // singleton shared by every mounted Toaster, so an orphaned callback
  // from an earlier, already-unmounted instance can still evict whatever
  // a LATER instance's own protected toast is currently showing).
  const pendingToastTimeoutsRef = useRef<Set<ReturnType<typeof setTimeout>>>(new Set())
  useEffect(() => {
    const pending = pendingToastTimeoutsRef.current
    return () => {
      pending.forEach(id => clearTimeout(id))
      pending.clear()
    }
  }, [])
  // Waits out any currently-protected outcome window, then shows AND
  // claims its own protected window — for send OUTCOMES (success/
  // failure), which deserve that guaranteed minimum visibility
  // (especially a failure's Retry action).
  //
  // Re-checks REAL elapsed time since the LAST ACTUAL display at every
  // fire attempt, including the first, rather than trusting a delay
  // computed once at scheduling time (Codex PR review, eleventh pass —
  // see outcomeToastScheduleQueryKey's own comment for the backgrounded-
  // tab/blocked-main-thread scenario this closes). This also means no
  // separate "reserve a future slot" bookkeeping is needed: two outcomes
  // queued at nearly the same real moment naturally serialize correctly
  // anyway, since JS's single-threaded execution guarantees the second
  // one's re-check always sees whatever the first one already did,
  // however close together they run.
  const queueOutcomeToast = (showOutcome: () => void) => {
    const attempt = () => {
      const delay = outcomeToastProtectedUntil() - Date.now()
      if (delay <= 0) {
        setOutcomeToastLastShownAt(Date.now())
        showOutcome()
      } else {
        const id = setTimeout(() => {
          pendingToastTimeoutsRef.current.delete(id)
          attempt()
        }, delay)
        pendingToastTimeoutsRef.current.add(id)
      }
    }
    attempt()
  }
  // Waits out any currently-protected outcome window, then shows — but
  // does NOT claim its own protected window (does not update
  // outcomeToastLastShownAt). For the "Sending…" placeholder specifically
  // (Codex PR review, seventh pass): it has no action to protect, so a
  // new send starting while an outcome is still in its window should
  // wait its turn to display rather than instantly evicting that
  // outcome, but it shouldn't ALSO block whatever comes after it. The
  // actual network call is never delayed by this — only when the toast
  // visually appears.
  //
  // Re-checks the CURRENT schedule when its timer actually fires, rather
  // than trusting the delay computed at scheduling time (Codex PR review,
  // eighth and eleventh passes): since the schedule is shared across
  // mounts (see outcomeToastScheduleQueryKey), a DIFFERENT window can
  // still be claimed — by a newer outcome, from either this or another
  // mount, or after a delay from tab throttling — after this was
  // scheduled but before its timer elapses. Re-scheduling for whatever's
  // left guarantees this can never fire during a window it didn't know
  // about yet, however many times that window gets extended in the
  // meantime.
  //
  // Returns a cancel function (Codex PR review, ninth pass): re-checking
  // the schedule on every fire only protects against firing INTO an
  // active window — it has no idea whether the SEND this placeholder
  // represents has already settled. If nothing else ever claims further
  // protection after this send's own outcome does, this callback would
  // otherwise eventually find the schedule clear and fire anyway,
  // resurrecting a stale "Sending…" for a request that finished (and
  // whose real outcome may already have been shown, and dismissed) long
  // before. performSend calls the returned cancel the instant its
  // request settles, independent of whatever the shared schedule says.
  const afterOutcomeProtection = (show: () => void): (() => void) => {
    let cancelled = false
    let currentId: ReturnType<typeof setTimeout> | null = null
    const attempt = () => {
      if (cancelled) return
      // afterOutcomeProtection only ever runs from performSend, itself
      // only ever started from a user-triggered event handler
      // (handleConfirmSend / a toast's Retry action), never during
      // render, so Date.now() here is safe — matches the identical
      // justification already established in reservation-details.tsx's
      // handleRegenerate.
      const delay = outcomeToastProtectedUntil() - Date.now()
      if (delay <= 0) {
        show()
      } else {
        const id = setTimeout(() => {
          pendingToastTimeoutsRef.current.delete(id)
          currentId = null
          attempt()
        }, delay)
        currentId = id
        pendingToastTimeoutsRef.current.add(id)
      }
    }
    attempt()
    return () => {
      cancelled = true
      if (currentId !== null) {
        clearTimeout(currentId)
        pendingToastTimeoutsRef.current.delete(currentId)
        currentId = null
      }
    }
  }
  const [phase, setPhase] = useState<Phase>('loading') // loading | ready | confirm | generating | error
  const [suggestion, setSuggestion] = useState<ThankYouSuggestion | null>(null)
  const form = useForm<ThankYouMessageValues>({
    resolver: zodResolver(thankYouMessageSchema),
    defaultValues: { text: '' },
  })
  const editedTextValue = useWatch({ control: form.control, name: 'text' })
  const [loadError, setLoadError] = useState<string | null>(null)
  // Which action a failure happened during — decides what "Try again"
  // should retry (Codex PR review: an earlier version always retried via
  // the mutating regenerate endpoint, even for a plain GET failure, which
  // created a needless new suggestion version instead of just retrying the
  // read).
  const [errorStage, setErrorStage] = useState<ErrorStage>('load') // 'load' | 'generate'
  const [idempotencyKey, setIdempotencyKey] = useState<string | null>(null)
  const [pendingSendBody, setPendingSendBody] = useState<string | null>(null)
  // Which reservation the dialog is CURRENTLY assigned to.
  const activeReservationRef = useRef<string | null>(null)
  // The highest suggestion version applied to the UI for the CURRENT
  // reservation. A same-reservation "Regenerate" and the dialog's own
  // initial load can both be in flight at once (operator clicks Regenerate,
  // closes the dialog, reopens it before the POST resolves — the reopen
  // fires its own GET) and there's no guarantee which one's response comes
  // back first (Codex PR review, third pass: an earlier fix only guarded
  // against a DIFFERENT reservation's stale response, not an OLDER response
  // for the SAME one racing a newer one). applyResult only ever accepts a
  // response whose version is >= this, so whichever response actually
  // reflects the latest data wins regardless of arrival order — never a
  // request-sequence token, which can't distinguish "old" from "new" by
  // itself. Reset to 0 whenever the active reservation changes.
  const appliedVersionRef = useRef(0)
  // Identifies the CURRENT effect-driven load attempt for the active
  // reservation, bumped every time the effect below runs. Unlike
  // activeReservationRef/appliedVersionRef (which intentionally let a
  // same-reservation manual Regenerate survive a close+reopen), this is
  // scoped to runLoad's own internal continuation only: if the operator
  // closes and reopens the SAME reservation quickly enough that a prior
  // load's GET is still in flight when it resolves with no suggestion
  // yet, that stale load must not go on to trigger ITS OWN regenerate call
  // — the newer load (or the newer load's own completed result) already
  // supersedes it (Codex PR review, fourth pass: without this, a stale
  // "null suggestion" GET response could launch an unrequested extra
  // regeneration after a fresher one had already completed and was being
  // displayed, eventually replacing it).
  const loadSeqRef = useRef(0)
  // Latest value of the `open` prop, kept in a ref so the staleness checks
  // below (closures created once per async attempt) can see a close that
  // happens after they started, not just the value at call time (Codex PR
  // review: without this, closing the dialog without reopening anything
  // left activeReservationRef/loadSeqRef/appliedVersionRef all unchanged,
  // so a poll already in flight kept issuing GETs — and rescheduling
  // itself — for up to its entire budget after the dialog was dismissed).
  // The cleanup return (Codex PR review, follow-up: the [open] effect above
  // only fires on a PROP transition, so navigating away from the page
  // unmounts this component without `open` ever changing, leaving the ref
  // stuck true) forces it false on unmount too — React runs a hook's
  // cleanup on every unmount regardless of whether its dependencies changed.
  const openRef = useRef(open)
  useEffect(() => {
    openRef.current = open
    return () => {
      openRef.current = false
    }
  }, [open])

  // Applies a completed suggestion to the UI if it's still relevant: same
  // reservation the dialog is currently showing, and STRICTLY newer than
  // whatever's already displayed (not just "not older" — Codex PR review,
  // fourth pass: two in-flight GETs for the same reservation, from a rapid
  // close/reopen, can both resolve with the SAME version. The faster one
  // applies it and the operator starts editing; the slower one, arriving
  // later with an equal version, used to pass the old `<` check and
  // silently overwrite the operator's in-progress edits with the original
  // draft text). Once a version has been applied, only a genuinely NEWER
  // one may replace it — applying the same version twice is a no-op.
  // Shared by both the load path and the manual-regenerate path so they
  // can never clobber each other out of order.
  //
  // If there's a retained pending-retry entry for THIS reservation (an
  // ambiguous send is still unresolved), that edited-but-unconfirmed text
  // is used as the displayed draft instead of the server's own
  // message_text (Codex PR review, fifth pass: the retry/idempotency KEY
  // already survived a close+reopen, but the DISPLAYED text didn't —
  // reopening showed the original unedited draft while the retained key
  // was for the operator's edited version. Confirming Send in that state
  // compared the newly-displayed original text against the retained
  // edited pendingSendBody, saw a mismatch, and minted a BRAND NEW
  // idempotency key with the ORIGINAL text — exactly the
  // double-send-with-different-content scenario idempotency exists to
  // prevent, if the first ambiguous send had actually reached Uplisting).
  // Reads getPendingRetry (the query cache) directly rather than the
  // pendingSendBody STATE variable: applyResult runs at the end of an
  // async chain (runLoad/pollUntilSettled/triggerRegenerate) that started
  // inside the open effect below, which ALSO calls setPendingSendBody —
  // but that's a state update, deferred to the next render, so this
  // closure (created during the render the effect fired from) would
  // still see the OLD pendingSendBody value if it read state here. The
  // query cache has no such staleness; it's always read fresh.
  const applyResult = (
    targetReservationId: string | null | undefined,
    completedSuggestion: ThankYouSuggestion
  ) => {
    if (activeReservationRef.current !== targetReservationId) return
    const version = completedSuggestion.version ?? 0
    if (version <= appliedVersionRef.current) return
    appliedVersionRef.current = version
    setSuggestion(completedSuggestion)
    const retainedBody = getPendingRetry(targetReservationId)?.pendingSendBody ?? null
    form.reset({ text: retainedBody ?? completedSuggestion.message_text ?? '' })
    setPhase('ready')
  }

  // Polls GET .../thank-you-suggestion until a version >= minVersion
  // settles into 'completed' or 'failed', or the attempt budget runs out.
  // Only called when something is already known to be 'queued' (either
  // observed via GET, or just returned by our own regenerate call) — never
  // for "nothing exists yet", which falls back to triggerRegenerate
  // immediately instead of polling. Shared by the initial load and the
  // manual Regenerate button so both can never diverge in how they decide
  // "still generating" vs "give up". minVersion guards against the same
  // stale-completed-version race SuggestedReviewBlock's identical
  // targetVersion tracking exists for: Workers KV is only eventually
  // consistent, so the very next GET after a regenerate call can still
  // return the previous, already-"completed" version for a beat.
  const pollUntilSettled = (
    targetReservationId: string,
    mySeq: number,
    versionAtStart: number,
    opts: { minVersion: number; attempt: number }
  ) => {
    const { minVersion, attempt } = opts
    const stillCurrent = () =>
      openRef.current &&
      activeReservationRef.current === targetReservationId &&
      loadSeqRef.current === mySeq &&
      appliedVersionRef.current === versionAtStart

    setTimeout(async () => {
      if (!stillCurrent()) return
      try {
        const { suggestion } = await api.getThankYouSuggestion(targetReservationId)
        if (!stillCurrent()) return
        if (suggestion && suggestion.version >= minVersion) {
          if (suggestion.status === 'completed') {
            applyResult(targetReservationId, suggestion)
            return
          }
          if (suggestion.status === 'failed') {
            setErrorStage('generate')
            setLoadError(suggestion.error || 'Could not draft a thank-you message.')
            setPhase('error')
            return
          }
        }
        const nextAttempt = attempt + 1
        if (nextAttempt >= MAX_ACTIVE_GENERATION_POLL_ATTEMPTS) {
          // Offer a plain re-check (via "Try again" -> handleRetryLoad)
          // rather than firing another regenerate on top of a job that may
          // still complete.
          setErrorStage('load')
          setLoadError('Still drafting — this is taking longer than expected.')
          setPhase('error')
          return
        }
        pollUntilSettled(targetReservationId, mySeq, versionAtStart, {
          ...opts,
          attempt: nextAttempt,
        })
      } catch (error: unknown) {
        if (!stillCurrent()) return
        setErrorStage('load')
        setLoadError(error instanceof Error ? error.message : 'Something went wrong.')
        setPhase('error')
      }
    }, POLL_INTERVAL_MS)
  }

  const runLoad = async (targetReservationId: string, mySeq: number) => {
    setPhase('loading')
    setLoadError(null)
    // Snapshot before this attempt does anything async — used by the
    // failure branches below to detect whether a NEWER attempt (for this
    // same reservation) already succeeded while this one was in flight
    // (Codex PR review, fifth pass: loadSeqRef alone only guards against a
    // newer LOAD superseding this one; it says nothing about a newer
    // manual Regenerate having already applied a fresher draft — a stale
    // failure here must not hide that already-displayed newer suggestion).
    const versionAtStart = appliedVersionRef.current
    const stillCurrent = () =>
      openRef.current &&
      activeReservationRef.current === targetReservationId &&
      loadSeqRef.current === mySeq &&
      appliedVersionRef.current === versionAtStart
    try {
      const { suggestion: existing } = await api.getThankYouSuggestion(targetReservationId)
      if (!stillCurrent()) return

      // A regenerate (or a queued draft this same dialog session already
      // observed) may have already committed to a version higher than
      // what this GET can see yet — most likely a close+reopen landing
      // between the regenerate call and that version propagating to KV.
      // Poll for THAT version rather than treating this as a fresh load,
      // so the reopened dialog never settles for a draft it already knows
      // is stale (Codex PR review, follow-up).
      const pendingTarget = getPendingTargetVersion(targetReservationId)
      if (pendingTarget > (existing?.version ?? 0)) {
        pollUntilSettled(targetReservationId, mySeq, versionAtStart, {
          minVersion: pendingTarget,
          attempt: 0,
        })
        return
      }

      if (existing && existing.status === 'completed') {
        applyResult(targetReservationId, existing)
        return
      }
      if (existing && existing.status === 'queued') {
        // Already generating (the proactive trigger, or a stale in-flight
        // regenerate) — poll for it instead of starting a duplicate.
        setPendingTargetVersion(targetReservationId, existing.version)
        pollUntilSettled(targetReservationId, mySeq, versionAtStart, {
          minVersion: existing.version,
          attempt: 0,
        })
        return
      }
      // No usable draft (never generated — this reservation predates the
      // proactive checkout-today trigger — or the last attempt failed) —
      // generate one now rather than showing an extra "Generate" click,
      // since opening this dialog already implies the operator wants a
      // draft to review.
      triggerRegenerate(targetReservationId, versionAtStart)
    } catch (error: unknown) {
      if (!stillCurrent()) return
      setErrorStage('load')
      setLoadError(error instanceof Error ? error.message : 'Something went wrong.')
      setPhase('error')
    }
  }

  // Triggers a fresh generation and polls for its result — shared by
  // runLoad's "nothing ever generated" fallback and the operator-facing
  // Regenerate/"Try again" button. The backend now enqueues this (see
  // suggestion-queue.js) rather than running it inline in the request, so
  // the response is a 'queued' placeholder, not the finished draft.
  const triggerRegenerate = async (targetReservationId: string, versionAtStart: number) => {
    // See runLoad's identical snapshot for why a stale FAILURE (not just a
    // stale success) needs this: if a newer load or regenerate for this
    // same reservation already applied a fresher draft while this request
    // was still in flight, a late failure from this one must not hide it.
    // A 'completed' result is deliberately NOT gated behind this check —
    // applyResult has its own, more precise staleness guard (same
    // reservation + strictly newer version), which correctly still applies
    // a fresher result even after a same-reservation close+reopen reset
    // appliedVersionRef to 0 in the meantime. openRef IS included here
    // (unlike applyResult's own guard) since a bare close with no reopen
    // at all should still stop a failure from surfacing an error toast/UI
    // for a dialog nobody is looking at.
    const stillCurrent = () =>
      openRef.current &&
      activeReservationRef.current === targetReservationId &&
      appliedVersionRef.current === versionAtStart
    setPhase('generating')
    try {
      const generated = await api.regenerateThankYouSuggestion(targetReservationId)
      if (generated.suggestion?.status === 'completed') {
        applyResult(targetReservationId, generated.suggestion)
        return
      }
      if (generated.suggestion?.status === 'failed') {
        if (!stillCurrent()) return
        setErrorStage('generate')
        setLoadError(generated.suggestion?.error || 'Could not draft a thank-you message.')
        setPhase('error')
        return
      }
      // 'queued' — pollUntilSettled has its own tick-level staleness guard
      // (keyed off the CURRENT loadSeqRef/versionAtStart snapshot below),
      // so it's safe to always start polling here rather than pre-checking
      // stillCurrent() first. Recording the target version (Codex PR
      // review, follow-up) is what lets a reopened SAME-reservation load
      // keep waiting for it, even if the reopen's own GET still only sees
      // the stale pre-regenerate draft.
      const targetVersion = generated.suggestion?.version ?? 1
      setPendingTargetVersion(targetReservationId, targetVersion)
      pollUntilSettled(targetReservationId, loadSeqRef.current, versionAtStart, {
        minVersion: targetVersion,
        attempt: 0,
      })
    } catch (error: unknown) {
      if (!stillCurrent()) return
      setErrorStage('generate')
      setLoadError(error instanceof Error ? error.message : 'Something went wrong.')
      setPhase('error')
    }
  }

  useEffect(() => {
    if (!open || !reservationId) return
    activeReservationRef.current = reservationId
    appliedVersionRef.current = 0

    // Load THIS reservation's own pending-retry entry, if any (Codex PR
    // review, fifth pass: keyed per reservation, not a single shared slot
    // — opening a different reservation must never touch another one's
    // still-pending retry state). A reopen of the SAME reservation (e.g.
    // after an ambiguous send failure) naturally gets the same entry back,
    // so a retry is still recognized as the same request instead of
    // creating a brand-new send; a genuinely different reservation gets
    // whatever entry (or none) belongs to it, independent of what the
    // previously-displayed reservation had pending.
    const retry = getPendingRetry(reservationId)
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setIdempotencyKey(retry?.idempotencyKey ?? null)
    setPendingSendBody(retry?.pendingSendBody ?? null)

    loadSeqRef.current += 1
    runLoad(reservationId, loadSeqRef.current)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, reservationId])

  // Reset ephemeral display state on close so reopening never shows stale
  // draft text. Deliberately does NOT clear idempotencyKey/pendingSendBody
  // here — if the dialog closed after a send whose result is ambiguous
  // (e.g. the response was lost but the message may have reached
  // Uplisting), discarding the key on close would let a reopen-and-retry
  // for the same reservation/text create a new key and send the guest a
  // duplicate message. Those two are only cleared by the load effect above
  // (when the operator moves on to a different reservation) or on a
  // confirmed send success below.
  useEffect(() => {
    if (open) return
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setPhase('loading')
    setSuggestion(null)
    form.reset({ text: '' })
    setLoadError(null)
  }, [open, form])

  const handleRegenerate = () => {
    if (!reservationId) return
    const myReservationId = reservationId
    // An explicit Regenerate is the operator deliberately asking for a
    // FRESH draft, abandoning any ambiguous-send retry still pending for
    // this reservation (Codex PR review, sixth pass: applyResult restores
    // a retained pendingSendBody whenever this reservation still has a
    // pending-retry entry — without clearing it here first, a newly
    // regenerated draft would be silently replaced by the OLD pre-
    // regenerate edit, and the next Send would reuse the old key/body
    // against the new suggestion version instead of showing what was just
    // regenerated). Abandoning the old retry key here no longer risks a
    // genuine duplicate send if that original attempt actually reached
    // Uplisting (Codex PR review, ninth pass corrected this file's
    // earlier assumption that it was an acceptable tradeoff): the
    // reservation-wide confirmedSent lock catches it regardless — once
    // that original send succeeds, sending the Regenerated draft under
    // ITS OWN new key is refused too, since the lock isn't scoped to the
    // specific version a send targeted. The only residual gap is two
    // sends that are BOTH still genuinely in flight at once, with
    // neither's outcome known yet when the other is confirmed — a
    // narrower window than what existed before this lock, not something
    // this Regenerate flow itself needs to additionally guard against.
    setIdempotencyKey(null)
    setPendingSendBody(null)
    setPendingRetry(myReservationId, null)
    triggerRegenerate(myReservationId, appliedVersionRef.current)
  }

  const handleRetryLoad = () => {
    if (!reservationId) return
    loadSeqRef.current += 1
    runLoad(reservationId, loadSeqRef.current)
  }

  // Single-click send: the operator has already reviewed/edited the draft
  // in the 'ready' phase textarea, so Send fires the real send directly —
  // there is no separate "Send this message?" confirm screen to click
  // through first. The message text visible in the textarea IS the
  // confirmation; requiring a second click added friction without adding
  // any actual review the operator hadn't already done by editing/reading
  // it here.
  const onSubmitEdit = (values: ThankYouMessageValues) => {
    if (!suggestion || !reservationId) return
    // Codex PR review (seventh and ninth passes, carried over from the
    // former confirm-step handler): a DIFFERENT, still-in-flight send for
    // this reservation — e.g. the operator reopened and re-edited or
    // Regenerated while an earlier ambiguous attempt was still pending —
    // can succeed between renders. Firing performSend here regardless
    // would send a genuine SECOND real message under a brand-new key the
    // backend has never seen, since idempotency dedupes by KEY, not by
    // reservation. Refuse once ANY version of this reservation is known to
    // have already sent — a thank-you goes to the guest once, not once per
    // draft version.
    if (isConfirmedSent(reservationId)) {
      toast({
        title: 'Already sent',
        description: 'This message was already sent while you were editing it.',
      })
      setPhase('ready')
      return
    }
    // Also refuse while an EARLIER attempt for this reservation is still
    // outstanding (Codex PR review, tenth pass): isConfirmedSent alone
    // only catches an earlier attempt whose SUCCESS is already known —
    // confirming again before that earlier one's response has arrived at
    // all would sail past that check and dispatch a second request while
    // the first is still pending. Both could then independently succeed.
    if (isSendInFlight(reservationId)) {
      toast({
        title: 'Send already in progress',
        description: 'A message for this guest is already being sent. Try again in a moment.',
      })
      return
    }
    let key = idempotencyKey
    let body = pendingSendBody
    if (!key || body !== values.text) {
      key = crypto.randomUUID()
      body = values.text
      setIdempotencyKey(key)
      setPendingSendBody(body)
      setPendingRetry(reservationId, { idempotencyKey: key, pendingSendBody: body })
    }
    // Close immediately instead of keeping the operator stuck on a blocking
    // modal for the length of the request — progress and the outcome are
    // reported via the toast in performSend instead. Capture everything the
    // send needs before closing, since closing clears the
    // reservationId/guestName props this dialog receives.
    const targetReservationId = reservationId
    const version = suggestion.version
    const recipientLabel = guestName || 'the guest'
    onOpenChange(false)
    void performSend(targetReservationId, version, body, key, recipientLabel)
  }

  // form.handleSubmit(onSubmitEdit) reads internal refs, so it's wrapped
  // here and invoked from this click handler rather than called inline
  // during render (same pattern as composer.tsx/ask-tab.tsx) — there's no
  // <form> element here (matching the original plain-div edit chrome), so
  // this is the only place the actual submission is triggered from.
  const handleSendClick = () => {
    void form.handleSubmit(onSubmitEdit)()
  }

  // Fires the actual send and reports progress through a toast rather than
  // the dialog itself — the operator has already reviewed and confirmed the
  // message by the time this runs (handleConfirmSend below), so there's
  // nothing left for a blocking modal to protect against. Takes its
  // arguments explicitly (not read from state) so a Retry from the toast's
  // action button can call it again with the exact same request after the
  // dialog has moved on to something else.
  const performSend = async (
    targetReservationId: string,
    version: number,
    body: string,
    key: string,
    recipientLabel: string
  ) => {
    // duration: Infinity suspends Radix Toast's own auto-dismiss timer
    // (see @radix-ui/react-toast's startTimer) so a send that takes a
    // while doesn't let the "Sending…" toast vanish on its own before an
    // outcome toast below replaces it — losing the failure message and
    // its Retry action silently. Scheduled via afterOutcomeProtection
    // (not shown unconditionally) so it can't evict a still-protected
    // outcome from a DIFFERENT send (Codex PR review, seventh pass) —
    // this only delays when the toast visually appears, never the actual
    // network call just below, which always fires immediately regardless.
    const cancelSendingToast = afterOutcomeProtection(() => {
      toast({
        title: 'Sending message…',
        description: `Sending your thank-you message to ${recipientLabel}.`,
        duration: Infinity,
      })
    })
    // Marks this reservation unsendable for as long as THIS attempt is
    // outstanding, independent of confirmedSent — see
    // sendInFlightQueryKey's own comment above for why a success-only
    // lock isn't enough to close the window a second confirm can land in
    // before this one's response arrives.
    setSendInFlight(targetReservationId, true)
    try {
      await api.sendThankYouSuggestion(targetReservationId, version, {
        body,
        idempotencyKey: key,
      })
      setSendInFlight(targetReservationId, false)
      // Cancel the "Sending…" placeholder the instant this settles (Codex
      // PR review, ninth pass) — independent of the shared schedule,
      // which has no way to know on its own that the send THIS callback
      // represents has already finished. Left uncancelled, a still-
      // pending callback could otherwise fire later regardless, once
      // nothing else is claiming protection, and resurrect a stale
      // "Sending…" for a request that's long since settled.
      cancelSendingToast()
      // Record the objective fact of the send succeeding immediately,
      // before anything that could be stale (a refresh GET) enters the
      // picture — see confirmedSentQueryKey's own comment above.
      markConfirmedSent(targetReservationId)
      // Only touch retry/display state if it's still THIS send's key — a
      // Regenerate or a fresh edit for the same reservation may have
      // already replaced it with a newer one while this request was in
      // flight, and that newer state must not be clobbered by a late
      // success here. Reads the per-reservation store, not a single shared
      // ref, so a concurrent send for a DIFFERENT reservation can never
      // affect this check either way.
      const stillOwnsRetryState = getPendingRetry(targetReservationId)?.idempotencyKey === key
      if (stillOwnsRetryState) {
        setPendingRetry(targetReservationId, null)
        // Only touch the RENDERED state if the dialog is currently showing
        // this exact reservation — otherwise it belongs to whatever OTHER
        // reservation is currently open and must not be touched.
        if (activeReservationRef.current === targetReservationId) {
          setIdempotencyKey(null)
          setPendingSendBody(null)
        }
      }
      // A fresh toast() call each time (not update()-ing a handle to the
      // "Sending…" toast) — under TOAST_LIMIT=1 (use-toast.ts), whichever
      // toast() call runs LAST is simply whatever's shown, so a new call
      // always displays regardless of what came before it, with no need
      // to track or dismiss a specific prior toast. Queued via
      // queueOutcomeToast (Codex PR review, sixth pass) rather than shown
      // immediately: two outcomes settling within milliseconds of each
      // other would otherwise instantly evict one another under the same
      // TOAST_LIMIT, which could hide a failure's Retry action before the
      // operator ever sees it. Reported BEFORE the best-effort refresh
      // below, not after (Codex PR review, fifth pass: the API client has
      // no timeout/abort — awaiting a stalled refresh before dismissing
      // the Infinity-duration pending toast could leave "Sending…"
      // showing forever even though the send had already succeeded).
      queueOutcomeToast(() => {
        toast({
          title: 'Message sent',
          description: `${recipientLabel} was sent your thank-you message.`,
        })
      })
      // Let the page re-read the row (see onSent's own comment): the send
      // has already changed server-side state this dialog does not own.
      // Deliberately not awaited and not gated on the dialog still showing
      // this reservation - the chase was marked regardless of what the
      // dialog moved on to. Placed after the outcome toast for the same
      // reason the best-effort refresh below is: nothing optional runs
      // before the operator has been told the send succeeded, so a throw
      // here can never present a delivered message as a failed one.
      onSent?.()
      // Codex PR review (P1): if the operator reopened this exact
      // reservation before this request settled, its own GET can have
      // already displayed the pre-send draft as sendable. Left alone, the
      // dialog would stay in that state — Send still enabled — and
      // confirming again would mint a brand-new idempotency key and fire a
      // genuine duplicate message. This refresh is purely to make an
      // already-open dialog re-render promptly and pick up the server's
      // authoritative sent_at/sent_by for display — fire-and-forget: not
      // awaited (see the toast-ordering comment just above).
      if (
        stillOwnsRetryState &&
        openRef.current &&
        activeReservationRef.current === targetReservationId
      ) {
        void (async () => {
          try {
            const { suggestion: refreshed } = await api.getThankYouSuggestion(targetReservationId)
            // refreshed.version >= version (the version WE just sent):
            // the eventually-consistent read behind this GET can still
            // return an OLDER version than what's displayed (Codex PR
            // review, eighth pass). Since confirmedSent's lock is
            // reservation-wide, not version-scoped (Codex PR review,
            // ninth pass), accepting a stale older response here can no
            // longer re-enable Send — alreadySent stays true regardless
            // of which version `suggestion` holds — but it would still
            // display the wrong sent_at/sent_by (the stale record's own,
            // likely still null) under the correct, already-sent text, so
            // this guard is now about display accuracy, not correctness.
            if (
              refreshed &&
              refreshed.version >= version &&
              openRef.current &&
              activeReservationRef.current === targetReservationId
            ) {
              // The exact text we know was sent — not refreshed.message_text,
              // which may not (yet, given the same eventual-consistency lag)
              // reflect the operator's edit, or may lag behind it entirely.
              setSuggestion({ ...refreshed, message_text: body })
              form.reset({ text: body })
              setPhase('ready')
            }
          } catch {
            // Best-effort — the confirmedSent lock above already keeps
            // Send disabled regardless, so a failed refresh here only means
            // the authoritative sent_at/sent_by take a beat longer to show.
          }
        })()
      }
    } catch (error: unknown) {
      // See the identical calls in the try block above — this attempt is
      // no longer outstanding either way, so the reservation is unlocked
      // for a genuine retry (still gated by isConfirmedSent, in case an
      // ambiguous failure here actually reached Uplisting and a
      // DIFFERENT concurrent attempt for this reservation already
      // recorded success by the time this one is retried).
      setSendInFlight(targetReservationId, false)
      cancelSendingToast()
      const reason =
        (error as { body?: { error?: string }; message?: string })?.body?.error ||
        (error instanceof Error ? error.message : undefined) ||
        'Please try again'
      queueOutcomeToast(() => {
        toast({
          title: 'Failed to send message',
          description: reason,
          variant: 'destructive',
          // Longer than the default 5s (Radix's own auto-dismiss) — a
          // failure with a Retry action deserves real time for a busy
          // operator to notice and act on before it disappears on its own.
          duration: 10000,
          action: (
            <ToastAction
              altText="Retry"
              onClick={() => {
                // Checked at CLICK time, not toast-creation time (Codex PR
                // review, sixth pass): the operator may have reopened this
                // reservation and Regenerated or re-edited the draft since
                // this failure, which replaces or clears this reservation's
                // pending-retry entry. Retrying with THIS toast's now-
                // superseded key/body would resurrect an abandoned draft
                // and could send it to the guest alongside whatever they
                // send next from the dialog — a real duplicate/wrong-
                // content-send risk, not just a stale-button annoyance.
                if (getPendingRetry(targetReservationId)?.idempotencyKey !== key) {
                  toast({
                    title: 'This draft has changed',
                    description:
                      'The message was edited or regenerated since this attempt. Reopen the dialog to send the current draft.',
                  })
                  return
                }
                // Also re-check the sent-confirmation lock (Codex PR
                // review, seventh and ninth passes): a DIFFERENT attempt
                // for this reservation — e.g. this failure's own key,
                // retried and now settled elsewhere, or a re-edit or
                // Regenerate confirmed while this one looked pending —
                // may have already succeeded since this toast appeared.
                // Reservation-wide, not tied to this specific version: a
                // thank-you is sent to the guest once, regardless of
                // which draft version it was.
                if (isConfirmedSent(targetReservationId)) {
                  toast({
                    title: 'Already sent',
                    description:
                      'This message was already sent. Reopen the dialog to see the current status.',
                  })
                  return
                }
                // And the in-flight lock (Codex PR review, tenth pass): a
                // DIFFERENT attempt for this reservation — e.g. a retry
                // from reopening and confirming again — may be pending
                // right now, in the window before ITS OWN success or
                // failure is known (so isConfirmedSent above wouldn't
                // catch it either). Retrying this one too would dispatch
                // a second concurrent request under a second key.
                if (isSendInFlight(targetReservationId)) {
                  toast({
                    title: 'Send already in progress',
                    description:
                      'A message for this guest is already being sent. Try again in a moment.',
                  })
                  return
                }
                void performSend(targetReservationId, version, body, key, recipientLabel)
              }}
            >
              Retry
            </ToastAction>
          ),
        })
      })
      // The dialog is already closed at this point — the retained
      // idempotency key/body (untouched above) is what lets the operator
      // reopen this same reservation's dialog later and have it come back
      // pre-filled with this exact unsent edit, ready to retry.
    }
  }

  // Trusts the local lock even when the (possibly stale) fetched record
  // doesn't show sent_at yet — see confirmedSentQueryKey's comment above.
  // Reservation-wide, not tied to suggestion?.version: once ANY version
  // of this reservation is known sent, every version (including a
  // Regenerated one that was itself never sent) stays locked.
  const alreadySent = !!suggestion?.sent_at || isConfirmedSent(reservationId)

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Thank-you message{guestName ? ` for ${guestName}` : ''}</DialogTitle>
          <DialogDescription>
            Review and edit the AI-drafted message, then send it to the guest via Uplisting. This
            can&apos;t be undone.
          </DialogDescription>
        </DialogHeader>

        {phase === 'loading' && (
          <div className="py-6 text-center text-sm text-textMuted">Drafting a message…</div>
        )}

        {phase === 'generating' && (
          <div className="py-6 text-center text-sm text-textMuted">Regenerating…</div>
        )}

        {phase === 'error' && (
          <div className="space-y-3">
            <p className="text-sm text-danger">{loadError}</p>
            <Button
              size="sm"
              variant="outline"
              onClick={errorStage === 'load' ? handleRetryLoad : handleRegenerate}
            >
              <RefreshCw className="mr-1.5 h-3.5 w-3.5" />
              Try again
            </Button>
          </div>
        )}

        {phase === 'ready' && alreadySent && (
          <div className="space-y-3">
            <div className="rounded-md border border-border bg-surface2 p-3 text-sm whitespace-pre-wrap leading-relaxed">
              {editedTextValue}
            </div>
            <p className="text-xs text-textMuted">
              Already sent {formatLocaleDateTime(suggestion?.sent_at, { fallback: '' })}
              {suggestion?.sent_by ? ` by ${suggestion.sent_by}` : ''}.
            </p>
          </div>
        )}

        {phase === 'ready' && !alreadySent && (
          <Form {...form}>
            <div className="space-y-3">
              <FormField
                control={form.control}
                name="text"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <textarea
                        {...field}
                        rows={7}
                        aria-label="Edit thank-you message before sending"
                        className="w-full text-sm leading-relaxed px-3 py-2 border border-border rounded-md bg-surface2 text-text focus:outline-hidden focus:ring-2 focus:ring-mint resize-y"
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
              <div className="flex items-center gap-2">
                <Button size="sm" onClick={handleSendClick} disabled={!editedTextValue?.trim()}>
                  Send
                </Button>
                <Button size="sm" variant="outline" onClick={handleRegenerate}>
                  <RefreshCw className="mr-1.5 h-3.5 w-3.5" />
                  Regenerate
                </Button>
              </div>
            </div>
          </Form>
        )}
      </DialogContent>
    </Dialog>
  )
}
